// eslint-disable-next-line no-restricted-imports
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react";
import clsx from "clsx";
import { FC, useEffect, useId, useMemo } from "react";

import { PlannerInsert } from "../section/planner/PlannerInsert";

import { SessionInProgressButton } from "./SessionInProgressButton";
import { isStaff } from "./roles";

import { serverGetHomePlannerPages, serverGetPageAssessmentStatuses } from "api/api-server-data";
import { CoreUserRoleType } from "components/server-types";
import { t } from "i18n/i18n";
import { Banner } from "mds/components/Banner";
import { IconText } from "mds/components/IconText";
import { ListItem } from "mds/components/ListItem";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { BookSectionIcon, ChevronDownIcon, ChevronUpIcon, EyeNotVisibleIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { slices, storeApi, useAppDispatch, useAppSelector } from "store/index";
import { selectSectionsForCurrentUser, selectTerms } from "store/selectors";
import { sortByTermYearRegex } from "store/selectors/sorting";
import { computedStateActions } from "store/slices/computed";
import { useIsArchived } from "utils/hooks/use-is-archived";
import { HomeRoleType, sectionHomeUrl } from "utils/urls";

export const HomeSectionList: FC<{ role: HomeRoleType }> = ({ role }) => {
  const id = useId();
  const dispatch = useAppDispatch();
  const terms = useAppSelector(selectTerms);
  const { canAuthorSection } = useCapabilities();
  const sectionsForSelectedRole = useAppSelector((s) => selectSectionsForCurrentUser(s, role));
  const { isActive, isArchived, sortByActiveTitles } = useIsArchived();
  const isSmallScreen = !useIsMdOrLarger();
  const canSeePlanner = isStaff(role);

  useEffect(() => {
    const loadPlannerData = async () => {
      const pages = await serverGetHomePlannerPages(role as CoreUserRoleType);
      if (pages?.length) {
        dispatch(slices.pages.actions.updateMany(pages));

        const pageIds = pages.map((p) => p.id);

        storeApi.page_access_data.list({ page_id__in: pageIds });
        const statuses = await serverGetPageAssessmentStatuses({ pageIds });

        dispatch(computedStateActions.savePageAssessmentStatuses(statuses));
      }
    };

    if (canSeePlanner) {
      loadPlannerData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSeePlanner, role]);

  const termsWithSections = useMemo(
    () =>
      terms.toSorted(sortByTermYearRegex).map((term) => {
        const sectionsForTerm = sectionsForSelectedRole
          .filter(
            (section) => section.term_id === term.id && (canAuthorSection || isActive(section)),
          )
          .toSorted(sortByActiveTitles);
        return {
          term,
          sections: sectionsForTerm,
        };
      }),
    [sectionsForSelectedRole, canAuthorSection, terms, isActive, sortByActiveTitles],
  ).filter((term) => term.sections.length > 0);

  if (sectionsForSelectedRole.length === 0) {
    return <Banner kind="info">{t("home.no_sections_for_role")}</Banner>;
  }

  return (
    <div className="flex h-full flex-col-reverse gap-6 overflow-y-auto md:flex-row md:items-start md:gap-10">
      <div className="flex flex-1 flex-col gap-8">
        {termsWithSections.map((termWithSections) => (
          <Disclosure key={termWithSections.term.id} defaultOpen>
            {({ open }) => {
              const titleId = `${id}-${termWithSections.term.id}`;
              return (
                <div>
                  <DisclosureButton
                    className="flex w-full items-center justify-between border-b border-black-tint-90 py-2 text-base font-medium text-black-tint-10"
                    title={open ? t("tooltip.collapse") : t("tooltip.expand")}
                  >
                    <span id={titleId}>{termWithSections.term.title}</span>
                    {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
                  </DisclosureButton>

                  <DisclosurePanel className="flex flex-col gap-2">
                    <ol aria-labelledby={titleId} className="flex flex-col">
                      {termWithSections.sections.map((section) => (
                        <ListItem
                          className="w-full justify-between first:border-t-0"
                          key={section.id}
                          kind="primary"
                          to={sectionHomeUrl(section.id)}
                        >
                          <div className="flex min-w-0 flex-auto">
                            <IconText
                              className={clsx("truncate", {
                                "text-black-tint-55": isArchived(section),
                              })}
                              iconStart={
                                isArchived(section) ? <EyeNotVisibleIcon /> : <BookSectionIcon />
                              }
                              text={<span className="min-w-12 truncate">{section.title}</span>}
                            />
                          </div>
                          <SessionInProgressButton sectionId={section.id} />
                        </ListItem>
                      ))}
                    </ol>
                  </DisclosurePanel>
                </div>
              );
            }}
          </Disclosure>
        ))}
      </div>

      {canSeePlanner && (
        <div className={clsx("flex-shrink-0", !isSmallScreen && "mb-6")}>
          <PlannerInsert role={role as CoreUserRoleType} startOpen={!isSmallScreen} />
        </div>
      )}
    </div>
  );
};
